import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { isString, isUndefined } from 'lodash';
import { Account } from '../../../account-data/account.model';
import { DocumentTypesEnum } from '../../../document-data/document-data.data';
import { Billing } from '../../../document-data/sub-models/billing.model';
import { CalculationResult } from '../../../document-data/sub-models/calculation-result.model';
import { Deposit } from '../../../document-data/sub-models/deposit.model';
import { States } from '../../../document-data/sub-models/states.model';
import { Toggles } from '../../../features-data/sub-models/toggles.model';
import { LocalizationService } from '../../../localization/localization.service';
import { IMessageDeliveryData } from '../../../message-delivery/message-delivery.interface';
import { CompanyInfo } from '../../../settings-data/sub-models/company-info.model';
import { EmailDefault } from '../../../settings-data/sub-models/company-settings/email-default.model';
import { EmailDefaults } from '../../../settings-data/sub-models/company-settings/email-defaults.model';
import { PaymentsSettings } from '../../../settings-data/sub-models/company-settings/payments.model';
import { DocumentPresetSettings } from '../../../settings-data/sub-models/document-preset-settings/document-preset-settings.model';
import { IIcon } from '../../../shared/components/icon/icon.interface';
import { IconSizesEnum } from '../../../shared/components/icon/icons-sizes.enum';
import {
  ITrackingObject,
  TrackingScreenNamesEnum,
} from '../../../tracking/tracking.data';
import {
  ContentSendBuilder,
  IDocumentMessageData,
} from './content-send.builder';
import { FeaturesDataStore } from '../../../features-data/features-data.store';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { Document } from '../../../document-data/document.model';
import { RendererService } from '../../../renderer/renderer.service';
import { takeUntil } from 'rxjs/operators';
import { serialize } from '../../../../utils/helpers/model.helper';
import { IDocumentDataDispatcher } from '../../../document-data/document-data-dispatcher.interface';
import { List } from 'immutable';
import { AttachmentView } from '../../../attachments/attachment-view.model';
import { Attachment } from '../../../attachments/attachment.model';
import { IPdfInfo } from '../../../renderer/renderer.interface';
import { SafeHtml } from '@angular/platform-browser';

@Component({
  selector: 'app-content-send',
  templateUrl: './content-send.component.html',
  styleUrls: ['./content-send.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  viewProviders: [ContentSendBuilder],
})
export class ContentSendComponent implements OnInit, OnChanges {
  @Input() attachments: List<AttachmentView>;
  @Input() fileAttachments: List<Attachment>;
  @Input() companyInfo: CompanyInfo;
  @Input() document: Document;
  @Input() documentBilling: Billing;
  @Input() documentCalculationResult: CalculationResult;
  @Input() documentNumber: string;
  @Input() documentSettings: DocumentPresetSettings;
  @Input() documentDueDate: string;
  @Input() documentLocale: string;
  @Input() documentType: DocumentTypesEnum;
  @Input() emailDefaults: EmailDefaults;
  @Input() showBankTransfer: boolean;
  @Input() showCardPayments: boolean;
  @Input() showPaypalEc: boolean;
  @Input() bankTransferEnabled: boolean;
  @Input() cardPaymentsEnabled: boolean;
  @Input() paypalEcEnabled: boolean;
  @Input() paymentsSettings: PaymentsSettings;
  @Input() documentTrackingObject: ITrackingObject;
  @Input() trackingScreenName: TrackingScreenNamesEnum;
  @Input() deposit: Deposit;
  @Input() featureToggles: Toggles;
  @Input() account: Account;
  @Input() documentState: States;
  @Input() pdfSize: number;
  @Input() documentDataDispatcher: IDocumentDataDispatcher;

  @Output()
  onMessageUpdated: EventEmitter<IMessageDeliveryData> = new EventEmitter<IMessageDeliveryData>();
  @Output()
  onStatusChanged: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() onGrowthCta: EventEmitter<void> = new EventEmitter<void>();

  private _destroyed: Subject<boolean> = new Subject<boolean>();
  attachmentSize$: BehaviorSubject<number> = new BehaviorSubject<number>(null);

  attachmentName: string;
  defaultCC: string;
  defaultBCC: string;
  defaultMessage: string;
  defaultSubject: string;
  defaultTo: string;
  hasBankTransfer: boolean;
  hasCardPayments: boolean;
  hasPaypalEc: boolean;
  paypalIcon: IIcon;
  showPaymentNote: boolean;
  isVerifiedAccount: boolean;
  DocumentTypesEnum: typeof DocumentTypesEnum;
  buttonCta: string;
  paymentNotificationsText: SafeHtml;
  isAccountNotified$: Observable<boolean>;

  constructor(
    private _localizationService: LocalizationService,
    private _featuresDataStore: FeaturesDataStore,
    private _contentSendBuilder: ContentSendBuilder,
    private _rendererService: RendererService
  ) {}

  ngOnChanges(changes: SimpleChanges) {
    if (
      this.featureToggles.isAllAccess(Toggles.KEYS.FILE_ATTACHMENT) &&
      (changes.bankTransferEnabled ||
        changes.cardPaymentsEnabled ||
        changes.paypalEcEnabled)
    ) {
      this.getAttachmentSize(
        this.document,
        this.bankTransferEnabled,
        this.cardPaymentsEnabled,
        this.paypalEcEnabled
      );
    }
    if (changes.showBankTransfer || changes.bankTransferEnabled) {
      this.hasBankTransfer = this.showBankTransfer && this.bankTransferEnabled;
    }

    if (changes.showCardPayments || changes.cardPaymentsEnabled) {
      this.hasCardPayments = this.showCardPayments && this.cardPaymentsEnabled;
    }

    if (changes.showPaypalEc || changes.paypalEcEnabled) {
      this.hasPaypalEc = this.showPaypalEc && this.paypalEcEnabled;
    }

    if (this.documentLocale) {
      const translations =
        this._localizationService.getInstantTranslations(this.documentLocale) ||
        this._localizationService.getInstantTranslations(
          this.documentLocale.split('-')[0]
        ) ||
        this._localizationService.getInstantTranslations(
          this._localizationService.getDefaultLocale()
        );

      if (
        translations &&
        this.companyInfo &&
        isString(this.documentNumber) &&
        this.emailDefaults &&
        this.documentBilling &&
        this.account &&
        this.featureToggles &&
        this.documentState &&
        this.documentSettings &&
        this.paymentsSettings &&
        !isUndefined(this.hasBankTransfer) &&
        !isUndefined(this.hasCardPayments) &&
        !isUndefined(this.hasPaypalEc)
      ) {
        const emailDefault: EmailDefault = this.emailDefaults.useDefault
          ? this.emailDefaults.default
          : this.emailDefaults[this.documentType];

        const messageData: IDocumentMessageData = this._contentSendBuilder.buildMessageData(
          translations,
          this.documentType,
          this.account,
          this.documentState,
          this.companyInfo,
          this.documentNumber,
          emailDefault,
          this.documentCalculationResult,
          this.documentDueDate,
          this.deposit,
          this.documentLocale,
          this.hasBankTransfer,
          this.hasCardPayments,
          this.hasPaypalEc,
          this.documentSettings,
          this.paymentsSettings,
          this.featureToggles
        );

        this.buttonCta = messageData.buttonCta;
        this.defaultSubject = messageData.subject;
        this.attachmentName = messageData.attachmentName;
        this.paymentNotificationsText = messageData.paymentNotificationsText;
        this.defaultMessage = messageData.message;
        this.defaultBCC = messageData.bcc;
        this.defaultCC = messageData.cc;
      }
    }

    if (changes.documentBilling && this.documentBilling) {
      this.defaultTo = this.documentBilling.email || '';
    }

    const triggerShowPaymentNoteChanges =
      changes.emailDefaults ||
      changes.documentType ||
      changes.showBankTransfer ||
      changes.showCardPayments ||
      changes.showPaypalEc;
    if (
      triggerShowPaymentNoteChanges &&
      this.emailDefaults &&
      this.documentType
    ) {
      this.showPaymentNote =
        this.emailDefaults.includePaymentNotification &&
        (this.showBankTransfer || this.showCardPayments || this.showPaypalEc) &&
        this.documentType === DocumentTypesEnum.invoice;
    }
  }

  ngOnInit() {
    this.DocumentTypesEnum = DocumentTypesEnum;
    this.paypalIcon = {
      name: 'card-paypal',
      size: IconSizesEnum.custom,
    };

    this.isAccountNotified$ = this._featuresDataStore.isAccountNotified$;
  }

  getAttachmentSize(
    document,
    bankTransferEnabled,
    cardPaymentsEnabled,
    paypalEcEnabled
  ): void {
    if (document) {
      const renderableDocument = serialize(document).toJS();
      this._rendererService
        .renderDocumentAsPdf(
          renderableDocument,
          bankTransferEnabled,
          cardPaymentsEnabled,
          paypalEcEnabled
        )
        .pipe(takeUntil(this._destroyed))
        .subscribe((body: IPdfInfo) => {
          this.attachmentSize$.next(body.size);
        });
    }
  }
}
